import {
  useEffect, useState, useRef, useMemo, 
} from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useReactToPrint } from 'react-to-print';
import { useVacancy } from '@services/query.service';
import { useIsJobCoach } from '@App/components/AuthProvider/auth-provider.context';
import { 
  useCurrentJobDestination,
} from '@App/components/SharedCompanyInfo/NearbyCompanySection/nearby-company-section.utils';
import { normalizeText } from '@services/utils.service';

import LegendControl from '@App/components/Map/controls/Legend/Legend';
import RouteOptionsControl from '@App/components/Map/controls/RouteOptions/RouteOptions';
import NearbyCompanySection from '@App/components/SharedCompanyInfo/NearbyCompanySection/NearbyCompanySection';
import { RouteInfo } from '@App/components/Map/controls/RouteInfo/RouteInfo';

import { Portal } from '@nazka/nazka.mapframe.components';
import JobVacancy from '@App/pages/JobVacancy/JobVacancy';
import MobilityPlan from '@App/pages/MobilityPlan/MobilityPlan';
import Map from '@App/components/Map/Map';

import './print.styl';

function Print({ setPrintNow }) {
  const [map, setMap] = useState();
  const contentRef = useRef(null);
  const { vacancyId } = useParams();

  const OIDN = useSelector(state => state.vacancy.OIDN);
  const homeAddress = useSelector(state => state.map.homeAddress);
  const { data: companyData, isLoading } = useVacancy(OIDN);

  const isJobCoach = useIsJobCoach();
  const useJobApplicationLocation = useSelector(state => state.vacancy.useJobApplicationLocation);

  const currentJobDestination = useCurrentJobDestination(companyData, vacancyId);
  
  const isAggregator = companyData?.properties?.isInterim;
  const legendControl = useMemo(() => new LegendControl(true, isAggregator, useJobApplicationLocation, homeAddress), [
    isAggregator, useJobApplicationLocation, homeAddress,
  ]);
  const routeOptionsControl = useMemo(() => new RouteOptionsControl(isJobCoach, true), [isJobCoach]);

  const reactToPrintFn = useReactToPrint({ 
    contentRef,
    onAfterPrint: () => {
      map.removeControl(legendControl);
      map.removeControl(routeOptionsControl);
      map.remove();
      setPrintNow(false);
    },
  });

  useEffect(() => {
    if (map && !homeAddress && companyData?.geometry?.coordinates) {
      map.jumpTo({
        center: companyData?.geometry?.coordinates,
        zoom: 14,
      });
    }
  }, [map, homeAddress, companyData]);

  useEffect(() => {
    if (map) {
      map.addControl(legendControl, 'bottom-right');
      if (homeAddress) map.addControl(routeOptionsControl, 'top-right');
    }
  }, [homeAddress, map, legendControl, routeOptionsControl]);

  useEffect(() => {
    if (map && companyData) {
      map.getSource('vacancy')?.setData(currentJobDestination ? {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [currentJobDestination.lng, currentJobDestination.lat],
        },
        properties: {
          isJobAggregator: companyData.properties?.isJobAggregator,
          hasHouseNb: companyData.properties?.CRAB_HNR,
        },
      } : { type: 'FeatureCollection', features: [] });
    }
  }, [map, homeAddress, currentJobDestination, companyData]);

  useEffect(() => {
    if (!map || isLoading) return;

    function print() {
      const canvas = document.querySelector('.print .maplibregl-canvas');
      if (!canvas) return;

      const img = document.createElement('img');
      img.className = 'maplibregl-canvas-img';

      const rect = canvas.getBoundingClientRect();
      img.style.width = `${rect.width}px`;
      img.style.height = `${rect.height}px`;

      canvas.parentNode.insertBefore(img, canvas);

      const dataURL = canvas.toDataURL('image/png');

      img.src = dataURL;
      img.style.display = 'block';

      canvas.style.display = 'none';
      reactToPrintFn();
    }

    if (homeAddress && currentJobDestination) map.once('moveend', () => map.once('idle', () => print()));
    else map.once('idle', () => print());
  }, [map, reactToPrintFn, setPrintNow, isLoading, homeAddress, currentJobDestination]);

  useEffect(() => {
    if (map) {
      if (useJobApplicationLocation) {
        map.setLayoutProperty('vacancy', 'visibility', 'none');
        map.setLayoutProperty('sollicitation', 'visibility', 'visible');
      } else {
        map.setLayoutProperty('vacancy', 'visibility', 'visible');
        map.setLayoutProperty('sollicitation', 'visibility', 'none');
      }
    }

    return () => {
      if (map) {
        map.setLayoutProperty('vacancy', 'visibility', 'none');
        map.setLayoutProperty('sollicitation', 'visibility', 'none');
      }
    }
  }, [map, useJobApplicationLocation]);

  const {
    NAZKA_NM, OND_VESTNR, companyActivities, mobilityOffer, 
  } = companyData?.properties || {};

  return (
    <Portal>
      <div className="print" ref={contentRef}>
        <div className="print__page">
          <div className="shared-company-info__header">
            <h2 className="shared-company-info__header__title">
              {normalizeText(NAZKA_NM)}
            </h2>
            {isJobCoach && (
              <p className="shared-company-info__header__subtitle">
                {`${companyActivities ? companyActivities.map(a => normalizeText(a?.Description)).join(', ') : ''}`}
                {`${!OND_VESTNR?.includes('NZKA-') ? ` | Vestiging: ${OND_VESTNR}` : ''}`}
              </p>
            )}
            <br />
            <JobVacancy />
          </div>
        </div>
        {mobilityOffer && (
          <div className="print__page">
            <MobilityPlan />
          </div>
        )}
        <Map
          forPrint
          map={map}
          setMap={setMap}
          setHoveredFeature={() => null}
          setClickedFeature={() => null}
        />
        {homeAddress && (
          <div className="print__page">
            <RouteInfo map={map} isJobCoach={isJobCoach} forPrint />
          </div>
        )}
        <div className="print__hidden">
          <NearbyCompanySection currentJobDestination={currentJobDestination} map={map} />
        </div>
      </div>
    </Portal>
  )
}

export default Print;